import { writable } from 'svelte/store';

export interface ResponsiveCSS {
  mobile?: string;   // Base styles (default)
  tablet?: string;   // >= 640px
  desktop?: string;  // >= 768px
  large?: string;    // >= 1024px
}

/**
 * Responsive CSS directive that applies mobile-first responsive styles
 * using container queries for component-level responsiveness.
 * 
 * Usage:
 * <div use:responsiveStyle={{
 *   mobile: 'margin: 1rem;',
 *   tablet: 'margin: 2rem;',
 *   desktop: 'margin: 3rem;'
 * }}>
 */
export function responsiveStyle(node: HTMLElement, styles: ResponsiveCSS | string) {
  let currentStyleElement: HTMLStyleElement | null = null;
  
  function applyStyles(stylesConfig: ResponsiveCSS | string) {
    // Remove previous style element
    if (currentStyleElement) {
      currentStyleElement.remove();
      currentStyleElement = null;
    }

    // Handle legacy string format
    if (typeof stylesConfig === 'string') {
      node.style.cssText = stylesConfig;
      return;
    }

    // Generate unique class name for this element
    const uniqueId = `responsive-${Math.random().toString(36).substr(2, 9)}`;
    node.classList.add(uniqueId);

    // Build CSS rules with mobile-first approach
    let cssRules = '';
    
    // Base mobile styles (no container query needed)
    if (stylesConfig.mobile) {
      cssRules += `.${uniqueId} { ${stylesConfig.mobile} }\n`;
    }
    
    // Tablet styles (>= 640px)
    if (stylesConfig.tablet) {
      cssRules += `@container (min-width: 640px) { .${uniqueId} { ${stylesConfig.tablet} } }\n`;
    }
    
    // Desktop styles (>= 768px)
    if (stylesConfig.desktop) {
      cssRules += `@container (min-width: 768px) { .${uniqueId} { ${stylesConfig.desktop} } }\n`;
    }
    
    // Large desktop styles (>= 1024px)
    if (stylesConfig.large) {
      cssRules += `@container (min-width: 1024px) { .${uniqueId} { ${stylesConfig.large} } }\n`;
    }

    // Create and inject style element
    if (cssRules) {
      currentStyleElement = document.createElement('style');
      currentStyleElement.textContent = cssRules;
      document.head.appendChild(currentStyleElement);
    }

    // Ensure the container has container-type set for container queries
    const computedStyle = window.getComputedStyle(node);
    if (!computedStyle.containerType || computedStyle.containerType === 'normal') {
      node.style.containerType = 'inline-size';
    }
  }

  // Initial application
  applyStyles(styles);

  return {
    update(newStyles: ResponsiveCSS | string) {
      applyStyles(newStyles);
    },
    destroy() {
      if (currentStyleElement) {
        currentStyleElement.remove();
      }
    }
  };
}

/**
 * Helper function to process CSS rules and add !important to each rule
 */
function processCSS(css: string): string {
  if (!css) return '';
  
  const rules = css.split(';').filter(rule => rule.trim());
  const importantRules = rules.map(rule => {
    const trimmedRule = rule.trim();
    if (trimmedRule && !trimmedRule.includes('!important')) {
      return trimmedRule + ' !important';
    }
    return trimmedRule;
  });
  return importantRules.join('; ') + (importantRules.length > 0 ? ';' : '');
}

/**
 * Helper function to convert legacy CSS format to responsive format
 */
export function parseResponsiveCSS(css: ResponsiveCSS | string): ResponsiveCSS {
  if (typeof css === 'string') {
    return { mobile: css };
  }
  
  // Process each breakpoint to add !important
  const processed: ResponsiveCSS = {};
  if (css.mobile) processed.mobile = processCSS(css.mobile);
  if (css.tablet) processed.tablet = processCSS(css.tablet);
  if (css.desktop) processed.desktop = processCSS(css.desktop);
  if (css.large) processed.large = processCSS(css.large);
  
  return processed;
}
